import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Globe, LogOut, Monitor, Puzzle, Terminal } from "lucide-react";
import type { ClientType, DeviceSession } from "../../api/sessions.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Badge, Button } from "../../ui/primitives.js";

const CLIENT: Record<ClientType, { readonly icon: LucideIcon; readonly label: string }> = {
  web: { icon: Globe, label: "Web-App" },
  desktop: { icon: Monitor, label: "Desktop-App" },
  extension: { icon: Puzzle, label: "Browser-Erweiterung" },
  other: { icon: Terminal, label: "Andere Anwendung" }
};

/** Activity within the last two minutes reads as "now"; the server updates it at most once a minute. */
function lastActive(session: DeviceSession, now: number): string {
  return session.current || now - session.lastSeenAt < 2 * 60 * 1000 ? "gerade aktiv" : `aktiv ${formatRelativeTime(session.lastSeenAt, now)}`;
}

/** Signed-in devices, newest activity first. Sessions other than the current one can be signed out. */
export function SessionList(props: {
  readonly sessions: ReadonlyArray<DeviceSession>;
  /** Id of the session being signed out right now. */
  readonly revoking: string | undefined;
  readonly onRevoke: (session: DeviceSession) => void;
  readonly empty: string;
}): ReactNode {
  const now = Date.now();
  if (props.sessions.length === 0) {
    return <p className="rounded-lg border border-dashed border-line-strong px-5 py-6 text-center text-sm text-mist">{props.empty}</p>;
  }
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-ink-2">
      {props.sessions.map((session: DeviceSession) => {
        const client = CLIENT[session.clientType];
        const Icon = client.icon;
        return (
          <li key={session.id} className="flex items-center gap-3 px-4 py-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-line bg-ink-3 text-flow" title={client.label}>
              <Icon size={15} aria-hidden="true" />
              <span className="sr-only">{client.label}</span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-medium text-foam">
                <span className="truncate">{session.deviceName}</span>
                {session.current ? <Badge tone="mint">Dieses Gerät</Badge> : null}
              </p>
              <p className="text-[0.8125rem] leading-relaxed text-mist-2">
                <span className="font-mono">{session.ip === "unknown" ? "IP unbekannt" : session.ip}</span>
                {" · "}
                <span title={formatDateTime(session.lastSeenAt)}>{lastActive(session, now)}</span>
                {" · "}
                <span>angemeldet {formatDateTime(session.createdAt)}</span>
              </p>
            </div>
            {session.current ? null : (
              <Button
                size="sm"
                variant="secondary"
                icon={LogOut}
                loading={props.revoking === session.id}
                disabled={props.revoking !== undefined && props.revoking !== session.id}
                aria-label={`${session.deviceName} abmelden`}
                onClick={(): void => props.onRevoke(session)}
              >
                Abmelden
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
